import React, { useState, useEffect, useMemo } from 'react';
import { Plus, CreditCard as Edit, Trash2, Shield, User as UserIcon, Mail, Calendar, Activity, Key, Eye, EyeOff, CheckCircle, XCircle, UserPlus, X, RefreshCw, AlertTriangle, Search } from 'lucide-react';
import { User, UserActivity } from '../types';
import authService from '../services/authService';
import { usersService } from '../services/supabaseService';
import { supabase } from '../services/supabaseService';
import UserModal from './UserModal';
import { useNotifications } from '../hooks/useNotifications';

const UserManagement: React.FC = () => {
  const { notifyUserCreated } = useNotifications();
  const [users, setUsers] = useState<User[]>([]);
  const [userActivities, setUserActivities] = useState<UserActivity[]>([]);
  const [pendingRegistrations, setPendingRegistrations] = useState<any[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [activeTab, setActiveTab] = useState<'users' | 'pending' | 'activities'>('users');
  const [userCreationError, setUserCreationError] = useState<string>('');
  const [userCreationSuccess, setUserCreationSuccess] = useState<string>('');
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState<{totalUsers: number; syncedUsers: number; orphanedUsers: number} | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [togglingUserId, setTogglingUserId] = useState<string | null>(null);

  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return users;
    const q = searchQuery.toLowerCase();
    return users.filter(u =>
      u.name.toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q) ||
      getRoleLabel(u).toLowerCase().includes(q)
    );
  }, [users, searchQuery]);

  useEffect(() => {
    loadUsers();
    loadUserActivities();
    loadPendingRegistrations();
  }, []);

  const loadUsers = () => {
    // Load users from Supabase - superadmin can see all users
    loadUsersFromSupabase();
  };

  const loadUsersFromSupabase = async () => {
    try {
      console.log('🔄 Cargando usuarios desde Supabase...');
      const userData = await usersService.getAll();
      console.log('📊 Usuarios cargados raw:', userData.length);
      console.log('📋 Primer usuario para debug:', userData[0]);
      setUsers(userData);
      console.log('✅ Usuarios cargados:', userData.length);

      // Pass the fresh count. Reading users.length inside checkSyncStatus would
      // see the previous render's value because React hasn't flushed setUsers
      // yet, and the orphanedUsers delta would be off by however many users
      // were just added/removed.
      await checkSyncStatus(userData.length);
    } catch (error) {
      console.error('❌ Error cargando usuarios:', error);
      // Fallback to empty array
      setUsers([]);
    }
  };

  const checkSyncStatus = async (publicUsersCount: number) => {
    try {
      const { data: authUsers, error: authError } = await supabase.rpc('get_auth_users_count');

      if (!authError && authUsers) {
        setSyncStatus({
          totalUsers: publicUsersCount,
          syncedUsers: authUsers,
          orphanedUsers: publicUsersCount - authUsers
        });
      }
    } catch (error) {
      console.warn('⚠️ No se pudo verificar el estado de sincronización:', error);
    }
  };

  const handleSyncUsers = async () => {
    if (!window.confirm('¿Sincronizar usuarios? Esto verificará y limpiará cualquier inconsistencia entre auth.users y public.users.')) {
      return;
    }

    setIsSyncing(true);
    try {
      console.log('🔄 Iniciando sincronización de usuarios...');

      // Run cleanup query
      const { error: cleanupError } = await supabase.rpc('cleanup_orphaned_users');

      if (cleanupError) {
        console.error('❌ Error en sincronización:', cleanupError);
        alert('❌ Error al sincronizar usuarios. Ver consola para detalles.');
        return;
      }

      // Reload users
      await loadUsersFromSupabase();

      alert('✅ Usuarios sincronizados correctamente.');
    } catch (error) {
      console.error('❌ Error sincronizando usuarios:', error);
      alert(`❌ Error al sincronizar usuarios: ${error.message}`);
    } finally {
      setIsSyncing(false);
    }
  };

  const loadUserActivities = async () => {
    try {
      console.log('🔄 Cargando actividad del sistema desde auth_audit_log...');
      const { authAuditLogService } = await import('../services/supabaseService');
      const logs = await authAuditLogService.getRecent(50);

      const activities: UserActivity[] = logs.map(log => ({
        id: log.id,
        userId: log.userId || 'unknown',
        action: getActionLabel(log.eventType),
        resource: 'auth',
        resourceId: log.id,
        timestamp: log.loggedAt,
        details: `${log.userEmail} - ${log.context || getEventDescription(log.eventType)}`
      }));

      setUserActivities(activities);
      console.log('✅ Actividades del sistema cargadas:', activities.length);
    } catch (error) {
      console.error('❌ Error cargando actividad del sistema:', error);
      setUserActivities([]);
    }
  };

  const getActionLabel = (eventType: string): string => {
    const labels: Record<string, string> = {
      'login': 'inició sesión',
      'logout': 'cerró sesión',
      'session_reload': 'recargó sesión',
      'token_refresh': 'renovó token',
      'role_sync': 'sincronizó rol',
      'session_change_detected': 'cambió de sesión',
      'integrity_violation': 'detectó violación de integridad',
      'deployment_detected': 'detectó despliegue',
      'session_validation': 'validó sesión',
      'forced_logout': 'fue desconectado forzosamente'
    };
    return labels[eventType] || eventType;
  };

  const getEventDescription = (eventType: string): string => {
    const descriptions: Record<string, string> = {
      'login': 'Acceso al sistema',
      'logout': 'Salida del sistema',
      'session_reload': 'Sesión recargada',
      'token_refresh': 'Token renovado',
      'role_sync': 'Rol sincronizado',
      'session_change_detected': 'Cambio de sesión detectado',
      'integrity_violation': 'Violación de integridad detectada',
      'deployment_detected': 'Nuevo despliegue detectado',
      'session_validation': 'Sesión validada',
      'forced_logout': 'Desconexión forzada'
    };
    return descriptions[eventType] || eventType;
  };

  const loadPendingRegistrations = () => {
    loadPendingRegistrationsFromSupabase();
  };

  const loadPendingRegistrationsFromSupabase = async () => {
    try {
      console.log('🔄 Cargando solicitudes de registro desde Supabase...');
      const { registrationRequestsService } = await import('../services/supabaseService');
      const requests = await registrationRequestsService.getAll();
      
      // Filter only pending requests
      const pendingRequests = requests.filter(req => req.status === 'pending');
      setPendingRegistrations(pendingRequests);
      console.log('✅ Solicitudes pendientes cargadas:', pendingRequests.length);
    } catch (error) {
      console.error('❌ Error cargando solicitudes:', error);
      setPendingRegistrations([]);
    }
  };

  const handleCreateUser = () => {
    setUserCreationError('');
    setUserCreationSuccess('');
    setEditingUser(null);
    setIsModalOpen(true);
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setIsModalOpen(true);
  };

  const handleSaveUser = (userData: Partial<User>) => {
    if (editingUser) {
      // Update existing user
      updateUserViaSupabase(userData);
    } else {
      // Create new user via Edge Function
      createUserViaEdgeFunction(userData);
    }
  };

  const updateUserViaSupabase = async (userData: Partial<User>) => {
    try {
      console.log('🔄 Actualizando usuario:', userData.email, userData);

      if (!editingUser) return;

      // Update basic user fields
      const updatedUser = await usersService.update(editingUser.id, userData);

      // Update has_crm_access if provided
      if ((userData as any).hasCrmAccess !== undefined) {
        await supabase
          .from('users')
          .update({ has_crm_access: (userData as any).hasCrmAccess })
          .eq('id', editingUser.id);
      }

      // Update local state
      setUsers(users.map(u => u.id === editingUser.id ? updatedUser : u));

      alert('✅ Usuario actualizado correctamente');
      setIsModalOpen(false);
      setEditingUser(null);

      // Reload users to get fresh data
      await loadUsersFromSupabase();

    } catch (error) {
      console.error('❌ Error actualizando usuario:', error);
      alert(`❌ Error al actualizar el usuario: ${error.message}`);
    }
  };

  const createUserViaEdgeFunction = async (userData: Partial<User>) => {
    try {
      setUserCreationError('');
      setUserCreationSuccess('');
      console.log('🔄 Creando usuario via Edge Function:', userData.email);

      // Check if email already exists locally first
      const existingUser = users.find(u => u.email.toLowerCase() === userData.email?.toLowerCase());
      if (existingUser) {
        setUserCreationError(`❌ El email ${userData.email} ya está registrado en el sistema. El usuario "${existingUser.name}" ya usa este email.`);
        return;
      }

      // Get current Supabase session for access token
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session?.access_token) {
        throw new Error('No se pudo obtener token de acceso');
      }

      console.log('🔑 Token obtenido, llamando Edge Function...');

      // Call Edge Function with proper payload
      const { data, error: functionError } = await supabase.functions.invoke('create_user', {
        body: {
          email: userData.email,
          full_name: userData.name,
          role: userData.role || 'user',
          role_id: (userData as any).roleId,
          sendInvite: (userData as any).sendInvite || false,
          has_crm_access: (userData as any).hasCrmAccess || false,
          is_collaborator: (userData as any).isCollaborator || false
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      // Check for function invocation errors first
      if (functionError) {
        console.error('❌ Function invocation error:', functionError);
        setUserCreationError(`Error de conexión con el servidor: ${functionError.message}`);
        return;
      }

      // Check the response data for success/error
      if (!data || data.success === false) {
        console.error('❌ Error from Edge Function:', data);
        
        // Handle specific error types
        let userFriendlyMessage = data?.error || 'Error creating user';
        
        if (data?.error?.includes('User already registered') || 
            data?.error?.includes('ya está registrado') ||
            data?.code === 'USER_ALREADY_EXISTS') {
          userFriendlyMessage = `El email ${userData.email} ya está registrado en el sistema. Usa un email diferente.`;
        } else if (data?.error?.includes('Invalid email')) {
          userFriendlyMessage = `El formato del email ${userData.email} no es válido.`;
        } else if (data?.error?.includes('Password')) {
          userFriendlyMessage = 'Error con la contraseña. Verifica los requisitos mínimos.';
        } else if (data?.error?.includes('Insufficient permissions')) {
          userFriendlyMessage = 'No tienes permisos suficientes para crear usuarios.';
        } else {
          userFriendlyMessage = `Error al crear el usuario: ${data?.error || 'Error desconocido'}`;
        }
        
        setUserCreationError(userFriendlyMessage);
        return; // Don't throw, just set error state
      }

      console.log('✅ Usuario creado via Edge Function:', data);

      // Reload users list
      await loadUsersFromSupabase();

      // Send notification for new user
      await notifyUserCreated(
        userData.name || '',
        userData.email || '',
        undefined
      );

      // Send welcome email with credentials
      if (data.temporary_password) {
        try {
          const { emailNotificationService } = await import('../services/emailNotificationService');
          await emailNotificationService.sendNewUserWelcomeEmail(
            userData.name || '',
            userData.email || '',
            data.temporary_password
          );
          console.log('✅ Email de bienvenida enviado a:', userData.email);
        } catch (emailError) {
          console.error('❌ Error enviando email de bienvenida:', emailError);
        }
      }

      let successMessage = `Usuario ${userData.name} creado correctamente.\n\n${data.message}`;
      successMessage += '\n\n⚠️ El usuario debe cambiar la contraseña en el primer acceso.';
      setUserCreationSuccess(successMessage);
      
      // Don't close modal immediately, let user see the success message
      setTimeout(() => {
        setIsModalOpen(false);
        setEditingUser(null);
        setUserCreationSuccess('');
      }, 3000);
      
    } catch (error) {
      console.error('❌ Error creando usuario:', error);
      setUserCreationError(`Error al crear el usuario: ${error.message}`);
    }
  };

  const handleToggleUserStatus = async (userId: string) => {
    const targetUser = users.find(u => u.id === userId);
    if (!targetUser) return;

    const newStatus = !targetUser.isActive;
    const action = newStatus ? 'activar' : 'desactivar';

    if (!window.confirm(`¿${action.charAt(0).toUpperCase() + action.slice(1)} el usuario "${targetUser.name}"?`)) return;

    setTogglingUserId(userId);
    try {
      await usersService.update(userId, { isActive: newStatus } as any);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, isActive: newStatus } : u));
    } catch (error: any) {
      console.error('Error toggling user status:', error);
      alert(`Error al ${action} el usuario: ${error.message}`);
    } finally {
      setTogglingUserId(null);
    }
  };

  const handleApproveRegistration = async (request: any) => {
    try {
      console.log('🔄 Aprobando solicitud:', request.email);

      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session?.access_token) {
        throw new Error('No se pudo obtener token de acceso');
      }

      // Approval sends a Supabase invite email; the user sets their own
      // password through that link. The old flow decoded btoa(passwordHash)
      // and seeded the user with that password (base64 is not encryption).
      const { error } = await supabase.functions.invoke('create_user', {
        body: {
          email: request.email,
          full_name: request.name,
          role: 'user',
          sendInvite: true
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (error) {
        console.error('❌ Error creating user:', error);
        throw new Error(error.message || 'Error creating user');
      }

      const { registrationRequestsService } = await import('../services/supabaseService');
      await registrationRequestsService.approve(request.id, session.user.id);

      await Promise.all([
        loadUsersFromSupabase(),
        loadPendingRegistrationsFromSupabase()
      ]);

      await notifyUserCreated(request.name, request.email);

      alert(`✅ Usuario ${request.name} aprobado. Se ha enviado un email de invitación a ${request.email} para que configure su contraseña y acceda al CRM.`);

    } catch (error: any) {
      console.error('❌ Error aprobando solicitud:', error);
      alert(`❌ Error al aprobar la solicitud: ${error.message}`);
    }
  };

  const handleRejectRegistration = async (request: any) => {
    const reason = window.prompt('Motivo del rechazo (opcional):');
    
    try {
      console.log('🔄 Rechazando solicitud:', request.email);
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        throw new Error('No se pudo obtener información del usuario actual');
      }

      const { registrationRequestsService } = await import('../services/supabaseService');
      await registrationRequestsService.reject(request.id, session.user.id, reason || undefined);
      
      // Reload pending registrations
      await loadPendingRegistrationsFromSupabase();
      
      alert(`✅ Solicitud de ${request.name} rechazada.`);
      
    } catch (error) {
      console.error('❌ Error rechazando solicitud:', error);
      alert(`❌ Error al rechazar la solicitud: ${error.message}`);
    }
  };

  const handleResetPassword = async (user: User) => {
    if (!window.confirm(`¿Enviar email de recuperación de contraseña a ${user.email}?`)) {
      return;
    }
    try {
      await authService.resetPassword(user.email);
      alert(`✅ Email de recuperación enviado a ${user.email}.`);
    } catch (err: any) {
      console.error('Error enviando reset:', err);
      alert(`❌ No se pudo enviar el email: ${err.message || 'error desconocido'}`);
    }
  };

  const handleDeleteUser = (userId: string) => {
    const user = users.find(u => u.id === userId);
    if (user && window.confirm(`¿Eliminar usuario ${user.name}? Esta acción eliminará el usuario del CRM.`)) {
      deleteUserFromCRM(userId, user);
    }
  };

  const deleteUserFromCRM = async (userId: string, user: User) => {
    try {
      setDeletingUserId(userId);
      console.log('🔄 Eliminando usuario del CRM:', user.email);
      
      // Get current session for authorization
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session?.access_token) {
        throw new Error('No se pudo obtener token de acceso');
      }

      // Call Edge Function using Supabase client (more reliable)
      console.log('🔄 Llamando Edge Function delete_user...');
      
      const { data, error: functionError } = await supabase.functions.invoke('delete_user', {
        body: {
          user_id: userId,
          user_email: user.email,
          user_name: user.name
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      // Check for function invocation errors
      if (functionError) {
        console.error('❌ Function invocation error:', functionError);
        throw new Error(`Error de conexión: ${functionError.message}`);
      }

      // Check the response from the Edge Function
      if (!data || data.success === false) {
        console.error('❌ Error from delete function:', data);
        throw new Error(data?.error || 'Error eliminando usuario');
      }

      console.log('✅ Usuario eliminado via Edge Function:', data);

      // Prepare success message based on deletion type
      let successMessage = data.message;

      if (data.was_orphaned) {
        successMessage += '\n\n⚠️ Este usuario solo existía en la base de datos local (usuario huérfano) y ha sido limpiado correctamente.';
      }

      // Update local state
      setUsers(users.filter(u => u.id !== userId));
      alert(successMessage);

      // Reload users to ensure sync
      await loadUsersFromSupabase();

    } catch (error) {
      console.error('❌ Error eliminando usuario:', error);

      // Provide more specific error messages
      let errorMessage = 'Error al eliminar el usuario: ';

      if (error.message.includes('Usuario no encontrado')) {
        errorMessage += 'El usuario no existe en el sistema. Puede que ya haya sido eliminado. Recargando lista...';
        // Reload to sync state
        await loadUsersFromSupabase();
      } else if (error.message.includes('Insufficient permissions')) {
        errorMessage += 'No tienes permisos suficientes para realizar esta acción.';
      } else {
        errorMessage += error.message;
      }

      alert(`❌ ${errorMessage}`);
    } finally {
      setDeletingUserId(null);
    }
  };
  const getRoleLabel = (user: User) => {
    const roleName = user.user_role?.name || user.role;
    const labels: Record<string, string> = {
      superadmin: 'Superadmin',
      admin: 'Administrador',
      usuario_basico: 'Usuario',
      colaborador_programador: 'Programador',
      colaborador_comercial: 'Comercial',
      user: 'Usuario'
    };
    return labels[roleName] || user.user_role?.display_name || 'Usuario';
  };

  const getRoleColor = (user: User) => {
    const roleName = user.user_role?.name || user.role;
    const colors: Record<string, string> = {
      superadmin: 'bg-red-100 text-red-800',
      admin: 'bg-orange-100 text-orange-800',
      usuario_basico: 'bg-blue-100 text-blue-800',
      colaborador_programador: 'bg-green-100 text-green-800',
      colaborador_comercial: 'bg-teal-100 text-teal-800',
      user: 'bg-blue-100 text-blue-800'
    };
    return colors[roleName] || 'bg-blue-100 text-blue-800';
  };

  const getUserActivitiesForUser = (userId: string) => {
    return userActivities.filter(activity => activity.userId === userId);
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestión de Usuarios</h1>
          <p className="text-gray-600">Administra usuarios y permisos del sistema</p>
          {syncStatus && syncStatus.orphanedUsers > 0 && (
            <div className="flex items-center space-x-2 mt-2 text-amber-600 text-sm">
              <AlertTriangle className="w-4 h-4" />
              <span>⚠️ {syncStatus.orphanedUsers} usuario(s) huérfano(s) detectado(s)</span>
            </div>
          )}
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleSyncUsers}
            disabled={isSyncing}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Sincronizar usuarios entre auth.users y public.users"
          >
            <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />
            <span>{isSyncing ? 'Sincronizando...' : 'Sincronizar'}</span>
          </button>
          <button
            onClick={handleCreateUser}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Nuevo Usuario</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Usuarios</p>
              <p className="text-2xl font-bold text-gray-900">{users.length}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <UserIcon className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Usuarios Activos</p>
              <p className="text-2xl font-bold text-gray-900">
                {users.filter(u => u.isActive).length}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Superadministradores</p>
              <p className="text-2xl font-bold text-gray-900">
                {users.filter(u => u.role === 'superadmin').length}
              </p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <Shield className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Actividades Hoy</p>
              <p className="text-2xl font-bold text-gray-900">
                {userActivities.filter(a => 
                  a.timestamp.toDateString() === new Date().toDateString()
                ).length}
              </p>
            </div>
            <div className="p-3 bg-orange-100 rounded-lg">
              <Activity className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="border-b border-gray-200">
          <div className="flex items-center justify-between px-6">
            <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('users')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'users'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Usuarios ({users.length})
            </button>
            <button
              onClick={() => setActiveTab('pending')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'pending'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Solicitudes Pendientes ({pendingRegistrations.length})
            </button>
            <button
              onClick={() => setActiveTab('activities')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'activities'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Actividad del Sistema ({userActivities.length})
            </button>
            </nav>
            {activeTab === 'users' && (
              <div className="relative py-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar usuarios..."
                  className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            )}
          </div>
        </div>

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Usuario
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rol
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Último Acceso
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Creado
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                      {searchQuery ? `No se encontraron usuarios con "${searchQuery}"` : 'No hay usuarios'}
                    </td>
                  </tr>
                )}
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <UserIcon className="h-5 w-5 text-blue-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{user.name}</div>
                          <div className="text-sm text-gray-500 flex items-center space-x-1">
                            <Mail className="w-3 h-3" />
                            <span>{user.email}</span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(user)}`}>
                        {getRoleLabel(user)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleUserStatus(user.id)}
                        disabled={togglingUserId === user.id}
                        className={`inline-flex items-center space-x-1 px-2 py-1 text-xs font-semibold rounded-full disabled:opacity-60 ${
                          user.isActive
                            ? 'bg-green-100 text-green-800 hover:bg-green-200'
                            : 'bg-red-100 text-red-800 hover:bg-red-200'
                        } transition-colors`}
                        title={user.isActive ? 'Click para desactivar' : 'Click para activar'}
                      >
                        {togglingUserId === user.id ? (
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></div>
                        ) : (
                          user.isActive ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />
                        )}
                        <span>{user.isActive ? 'Activo' : 'Inactivo'}</span>
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {(() => {
                        try {
                          if (!user.lastLogin) return 'Nunca';
                          if (!(user.lastLogin instanceof Date)) return 'Fecha inválida';
                          return user.lastLogin.toLocaleDateString();
                        } catch (error) {
                          console.error('Error formatting lastLogin:', error, user.lastLogin);
                          return 'Error en fecha';
                        }
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {(() => {
                        try {
                          if (!user.createdAt) return 'Fecha no disponible';
                          if (!(user.createdAt instanceof Date)) return 'Fecha inválida';
                          return user.createdAt.toLocaleDateString();
                        } catch (error) {
                          console.error('Error formatting createdAt:', error, user.createdAt);
                          return 'Error en fecha';
                        }
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => setSelectedUser(user)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded"
                          title="Ver actividad"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleResetPassword(user)}
                          className="text-orange-600 hover:text-orange-900 p-1 rounded"
                          title="Resetear contraseña"
                        >
                          <Key className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEditUser(user)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded"
                          title="Editar usuario"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        {user.role !== 'superadmin' && user.user_role?.name !== 'superadmin' && (
                          <button
                            onClick={() => handleDeleteUser(user.id)}
                            disabled={deletingUserId === user.id}
                            className="text-red-600 hover:text-red-900 p-1 rounded disabled:opacity-50"
                            title="Eliminar usuario"
                          >
                            {deletingUserId === user.id ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pending Registrations Tab */}
        {activeTab === 'pending' && (
          <div className="p-6">
            {pendingRegistrations.length > 0 ? (
              <div className="space-y-4">
                {pendingRegistrations.map((request) => (
                  <div key={request.id} className="border border-gray-200 rounded-lg p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0 h-12 w-12">
                          <div className="h-12 w-12 rounded-full bg-yellow-100 flex items-center justify-center">
                            <UserPlus className="h-6 w-6 text-yellow-600" />
                          </div>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-medium text-gray-900">{request.name}</h3>
                          <p className="text-sm text-gray-600 flex items-center space-x-1">
                            <Mail className="w-3 h-3" />
                            <span>{request.email}</span>
                          </p>
                          <p className="text-xs text-gray-500 mt-2 flex items-center space-x-1">
                            <Calendar className="w-3 h-3" />
                            <span>Solicitado: {request.requestedAt.toLocaleString()}</span>
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => handleApproveRegistration(request)}
                          className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                        >
                          <CheckCircle className="w-4 h-4" />
                          <span>Aprobar</span>
                        </button>
                        <button
                          onClick={() => handleRejectRegistration(request)}
                          className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                        >
                          <XCircle className="w-4 h-4" />
                          <span>Rechazar</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <UserPlus className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No hay solicitudes pendientes</h3>
                <p className="text-gray-500">Las nuevas solicitudes de registro aparecerán aquí</p>
              </div>
            )}
          </div>
        )}

        {/* Activities Tab */}
        {activeTab === 'activities' && (
          <div className="p-6">
            <div className="space-y-4">
              {userActivities.map((activity) => {
                const user = users.find(u => u.id === activity.userId);
                return (
                  <div key={activity.id} className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
                    <div className="flex-shrink-0 h-8 w-8">
                      <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <Activity className="h-4 w-4 text-blue-600" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        {user?.name || activity.details.split(' - ')[0] || 'Usuario desconocido'} {activity.action}
                      </p>
                      {activity.details && (
                        <p className="text-sm text-gray-600">{activity.details.includes(' - ') ? activity.details.split(' - ').slice(1).join(' - ') : activity.details}</p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        {activity.timestamp.toLocaleString()}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* User Modal */}
      <UserModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingUser(null);
          setUserCreationError('');
          setUserCreationSuccess('');
        }}
        onSave={handleSaveUser}
        user={editingUser}
        error={userCreationError}
        success={userCreationSuccess}
        onClearError={() => setUserCreationError('')}
        onClearSuccess={() => setUserCreationSuccess('')}
      />
    </div>
  );
};

export default UserManagement;